import { useEffect, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { trackEvent } from "@/lib/analytics";
import { useLocalizedSeo } from "@/hooks/useLocalizedSeo";
import {
  SUPPORTED_SEO_LANGS,
  type SeoLang,
} from "@/lib/seo-config";
import { urlFor, originFor } from "@/lib/domain-config";
import {
  buildBreadcrumbJsonLd,
  buildFaqJsonLd,
  buildSpeakableJsonLd,
} from "@/hooks/useJsonLd";
import { useScrollFadeIn, fadeCls } from "@/hooks/useScrollFadeIn";
import RelarkHero from "@/components/landing/relark/RelarkHero";
import RelarkPainPoint from "@/components/landing/relark/RelarkPainPoint";
import RelarkHowItWorks from "@/components/landing/relark/RelarkHowItWorks";
import RelarkAudienceFit from "@/components/landing/relark/RelarkAudienceFit";
import RelarkFounderNote from "@/components/landing/relark/RelarkFounderNote";
import RelarkFounderAnchor from "@/components/landing/relark/RelarkFounderAnchor";
import RelarkThreeTierStack from "@/components/landing/relark/RelarkThreeTierStack";
import RelarkRoiSnippet from "@/components/landing/relark/RelarkRoiSnippet";
import RelarkTrustBar from "@/components/landing/relark/RelarkTrustBar";
import RelarkComplianceSection from "@/components/landing/relark/RelarkComplianceSection";
import RelarkSixFactors from "@/components/landing/relark/RelarkSixFactors";
import NordicResearchProof from "@/components/landing/relark/NordicResearchProof";

const RelarkSampleInsight = lazy(
  () => import("@/components/landing/relark/RelarkSampleInsight"),
);
const RelarkSocialProof = lazy(
  () => import("@/components/landing/relark/RelarkSocialProof"),
);
const RelarkMeetingsShowcase = lazy(
  () => import("@/components/landing/relark/RelarkMeetingsShowcase"),
);

/* ------------------------------------------------------------------ */
/* Section: FAQ — 8 spørgsmål fra det nordiske CRO-manuskript          */
/* ------------------------------------------------------------------ */
const FAQ_KEYS = [
  // Skeptic-first ordering: a Nordic visitor screens for surveillance,
  // ChatGPT-wrapper risk, and regulatory posture BEFORE they consider
  // clicking. Activation ("trial" = "what happens when I click Start")
  // is parked third-from-last — only relevant once they're already sold.
  "surveillance",
  "chatgptWrapper",
  "highRisk",
  "nordicArchetypes",
  "whatIfWrong",
  "juniorAndWorkload",
  "tailoredValues",
  "upwardReactions",
  "languages",
  "trial",
  "time",
  "selvledelse",
] as const;

function FaqSection() {
  const { t } = useTranslation("ui");
  const { ref, isVisible } = useScrollFadeIn();

  return (
    <section className="bg-accent/20 py-16 sm:py-24">
      <div ref={ref} className="mx-auto max-w-3xl px-5">
        <div className={`text-center mb-10 ${fadeCls(isVisible)}`}>
          <h2 className="text-2xl font-bold sm:text-3xl">{t("relark.faq.title")}</h2>
        </div>
        <div className="space-y-3">
          {FAQ_KEYS.map((k) => (
            <details
              key={k}
              className="group rounded-2xl border border-border bg-card p-5 open:bg-background"
            >
              <summary className="cursor-pointer list-none flex items-center justify-between font-semibold gap-4">
                <span>{t(`relark.faq.items.${k}.q`)}</span>
                <span className="text-muted-foreground group-open:rotate-45 transition-transform shrink-0">
                  +
                </span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {t(`relark.faq.items.${k}.a`)}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Section: Final CTA                                                  */
/* ------------------------------------------------------------------ */
function FinalCtaSection() {
  const { t } = useTranslation("ui");
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollFadeIn();

  return (
    <section className="bg-primary text-primary-foreground py-16 sm:py-24">
      <div ref={ref} className={`mx-auto max-w-3xl px-5 text-center ${fadeCls(isVisible)}`}>
        <h2 className="text-2xl font-bold sm:text-3xl">{t("relark.finalCta.title")}</h2>
        <p className="mt-4 text-primary-foreground/85">{t("relark.finalCta.sub")}</p>
        <Button
          size="lg"
          variant="secondary"
          className="mt-8 h-auto min-h-[52px] w-full sm:w-auto rounded-full px-10 py-3.5 text-base font-semibold shadow-md"
          onClick={() => {
            trackEvent("relark_cta_click", { label: "final_describe_situation" });
            navigate("/onboarding");
          }}
        >
          {t("relark.finalCta.cta")}
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
        <p className="mt-4 text-sm text-primary-foreground/75">
          {t("relark.finalCta.trustLine")}
        </p>
      </div>
    </section>
  );
}

/* ================================================================== */
/* Page — nordisk CRO-rækkefølge:                                       */
/* problem → forståelse → bevis → logik → tryghed → handling           */
/* ================================================================== */
export default function RelarkLandingPage() {
  const { t, i18n } = useTranslation("ui");
  useLocalizedSeo(i18n.language);

  useEffect(() => {
    trackEvent("relark_landing_view");
  }, []);

  // JSON-LD per language
  useEffect(() => {
    const lang = (SUPPORTED_SEO_LANGS as string[]).includes(i18n.language)
      ? (i18n.language as SeoLang)
      : "da";
    const pageUrl = urlFor(lang);
    const originRoot = originFor(lang);

    const faqJson = buildFaqJsonLd(
      FAQ_KEYS.map((k) => ({
        question: t(`relark.faq.items.${k}.q`),
        answer: t(`relark.faq.items.${k}.a`),
      })),
      pageUrl,
    );
    const breadcrumb = buildBreadcrumbJsonLd([{ name: "Relark", path: "/" }], originRoot);
    const speakable = buildSpeakableJsonLd(
      ["h1", "[data-speakable='hero-sub']"],
      pageUrl,
    );

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld-relark", "true");
    script.textContent = JSON.stringify([faqJson, breadcrumb, speakable]);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [t, i18n.language]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader showSignIn showBuyersAnchor />

      {/* 1. Hero */}
      <RelarkHero />

      {/* 1b. Trust bar */}
      <RelarkTrustBar />

      {/* 2. Problem / agitation */}
      <RelarkPainPoint />

      {/* 2a. Forsknings-baseret social proof — "du er ikke alene" */}
      <NordicResearchProof />

      {/* 2b. NFA 6-faktor diagnose — navngiver smerten i målgruppens eget sprog */}
      <RelarkSixFactors />

      {/* 3. Sådan virker det — kommer FØR demo, så Anders giver mening */}
      <RelarkHowItWorks />

      {/* 3b. Hvad koster det at vente? — HR/budget-vinkel placeret ÉN gang, lige efter how-it-works */}
      <RelarkRoiSnippet />

      {/* 4. Live eksempel (Anders) — bevis #1 */}
      <Suspense fallback={<div className="py-24" />}>
        <RelarkSampleInsight />
      </Suspense>

      {/* 4b. Founder-anker — slim strip lige efter første proof. Tidlig
           differentiering: "her er hvem der har bygget det" inden den
           skeptiske besøgende beslutter om de vil læse videre. Linker til
           den fulde founder-sektion længere nede via #founder. */}
      <RelarkFounderAnchor />

      {/* 5. Mødeforberedelse (Anna-brief) — bevis #2, ryg-mod-ryg */}
      <Suspense fallback={<div className="py-24" />}>
        <RelarkMeetingsShowcase />
      </Suspense>

      {/* 7. Testimonials — bevis lige efter ROI-anker */}
      <Suspense fallback={<div className="py-16" />}>
        <RelarkSocialProof />
      </Suspense>

      {/* 8. Founder-stemme — naturlig overgang fra "andre stoler på det" → "her er hvem vi er" */}
      <RelarkFounderNote />

      {/* 9. Compliance & Sikkerhed — samlet med tabs (Sikkerhed/AI Act) */}
      <RelarkComplianceSection />

      {/* 10. Hvem er det til + Vi siger fra — kvalifikation lige før pris */}
      <RelarkAudienceFit />

      {/* 11. Tre lag — pris efter al værdi-bygning */}
      <RelarkThreeTierStack />

      {/* 12. FAQ — sidste indvendings-knock-down */}
      <FaqSection />

      {/* 13. Afsluttende CTA */}
      <FinalCtaSection />

      <PublicFooter showEthics showContact brand="relark" />
    </div>
  );
}
